import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setLogin, setLogout } from '../store/slices/auth';
import StorageService from '../services/storageService';
import { useRefreshToken } from './useRefreshToken';
import { useQueryClient } from '@tanstack/react-query';

export const useAuthInit = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: refreshApi } = useRefreshToken();
  const refreshInProgress = useRef(false);
  const retryCount = useRef(0);
  const maxRetries = 3;
  
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = StorageService.getAccessToken();
      const refreshTokenValue = StorageService.getRefreshToken();
      const userInfo = StorageService.getUserInfo();

      if (!accessToken || !refreshTokenValue || !userInfo) {
        StorageService.clearAuthOnly();
        queryClient.clear();
        dispatch(setLogout());
        return;
      }

      // Initialize Redux state with stored user data
      dispatch(setLogin({
        user: userInfo,
        token: accessToken
      }));
    };

    initializeAuth();

    // OPTIMIZED: Single refresh function with consistent timing
    const handleRefresh = async (isRetry = false) => {
      // Prevent multiple simultaneous refresh attempts
      if (refreshInProgress.current && !isRetry) {
        return;
      }

      const accessToken = StorageService.getAccessToken();
      const refreshToken = StorageService.getRefreshToken();
      
      if (!accessToken || !refreshToken) {
        return;
      }

      // OPTIMIZED: Single refresh window - 2 hours before expiry (since tokens last 24 hours)
      const willExpireSoon = StorageService.willTokenExpireSoon(2 * 60 * 60 * 1000); // 2 hours
      
      if (!willExpireSoon && !isRetry) {
        return;
      }

      refreshInProgress.current = true;
      
      try {
        const newTokens = await refreshApi();
        if (newTokens) {
          StorageService.saveTokens(newTokens);
          dispatch(setLogin({
            user: StorageService.getUserInfo(),
            token: newTokens.accessToken,
          }));
          retryCount.current = 0; // Reset retry count on success
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        
        // Retry logic for network errors
        if (retryCount.current < maxRetries && isRetry === false) {
          retryCount.current++;
          setTimeout(() => {
            refreshInProgress.current = false;
            handleRefresh(true);
          }, 5000);
          return;
        }
        
        // If all retries failed or it's not a retry, logout
        console.error('All refresh attempts failed, logging out');
        StorageService.clearAuthOnly();
        queryClient.clear();
        dispatch(setLogout());
        retryCount.current = 0;
      } finally {
        refreshInProgress.current = false;
      }
    };

    // OPTIMIZED: User activity detection - only refresh if token expires within 3 hours
    const handleUserActivity = () => {
      if (StorageService.willTokenExpireSoon(3 * 60 * 60 * 1000)) { // 3 hours
        handleRefresh();
      }
    };

    // Add user activity listeners
    const activityEvents = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // OPTIMIZED: Check every 30 minutes since tokens now last 24 hours
    const intervalId = setInterval(() => {
      handleRefresh();
    }, 30 * 60 * 1000); // 30 minutes

    // Initial refresh check
    handleRefresh();

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [dispatch, refreshApi]);
};
